import { useEffect, useState } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import CampaignTabs from "../../components/CampaignTabs";
import WaitTab from "./WaitTab";
import CompleteTab from "./CompleteTab";
import ReusableSearchBar from "../../components/ReusableSearchBar";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  cpsAdminSettlementDetail,
  cpsAdminSettlementRequest,
  cpsAdminSettlementsList,
  cpsAdminSettlementPayCommissions,
  settlementCompleted,
  settlementWating,
} from "../../api";
import { useToast } from "@chakra-ui/react";

export default function SettlementPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const toast = useToast();

  // 탭 → API 매핑
  const apiMap = {
    wait: settlementWating,
    complete: settlementCompleted,
  };

  const currentTab = Object.keys(apiMap).find((key) => pathname.includes(key));
  // 리스트 상태
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState();
  const [cpsItems, setCpsItems] = useState([]);
  const [cpsLoading, setCpsLoading] = useState(false);
  const [cpsPayBusyId, setCpsPayBusyId] = useState(null);

  const [keyword, setKeyword] = useState("");

  const cpsStatusByTab = {
    wait: ["DRAFT", "REQUESTED"],
    complete: ["CONFIRMED"],
  };

  const groupCpsByCampaign = (rows) => {
    const byCampaign = new Map();
    rows.forEach((row) => {
      const key = `${row.campaignId ?? "unknown"}-${row.campaignTitle ?? ""}`;
      if (!byCampaign.has(key)) {
        byCampaign.set(key, {
          campaignId: row.campaignId,
          campaignTitle: row.campaignTitle ?? "캠페인",
          totalSalesCount: 0,
          totalQuantity: 0,
          totalClicks: 0,
          manuscriptFee: 0,
          manuscriptFeePlatformFee: 0,
          totalCommission: 0,
          totalPlatformFee: 0,
          totalSalesAmount: 0,
          roas: 0,
          creators: [],
        });
      }
      const target = byCampaign.get(key);
      target.totalSalesCount += Number(row.totalSalesCount ?? 0);
      target.totalQuantity += Number(row.totalQuantity ?? 0);
      target.totalClicks += Number(row.totalClicks ?? 0);
      target.manuscriptFee += Number(row.manuscriptFee ?? 0);
      target.manuscriptFeePlatformFee += Number(row.manuscriptFeePlatformFee ?? 0);
      target.totalCommission += Number(row.totalCommission ?? 0);
      target.totalPlatformFee += Number(row.totalPlatformFee ?? 0);
      target.totalSalesAmount += Number(row.totalSalesAmount ?? 0);
      target.creators.push(row);
    });
    return Array.from(byCampaign.values()).map((c) => {
      const denom =
        Number(c.manuscriptFee ?? 0) +
        Number(c.manuscriptFeePlatformFee ?? 0) +
        Number(c.totalCommission ?? 0) +
        Number(c.totalPlatformFee ?? 0);
      return {
        ...c,
        roas: denom > 0 ? Math.round((Number(c.totalSalesAmount ?? 0) * 1000) / denom) / 10 : 0,
      };
    });
  };

  // 공통 API 호출 함수
  const loadItems = async () => {
    try {
      setLoading(true);

      const apiFn = apiMap[currentTab];
      if (!apiFn) return;

      const res = await apiFn();
      setItems(res?.data?.items ?? []); // 반드시 추가!
    } catch (e) {
      console.error("loadItems error:", e);
    } finally {
      setLoading(false);
    }
  };

  const loadCountsWithCps = async () => {
    try {
      // 1) CPUC/CPUR counts (waiting + completed)
      const [waitingRes, completedRes] = await Promise.all([
        settlementWating(),
        settlementCompleted(),
      ]);

      const waitingCounts = waitingRes?.data?.counts ?? {};
      const completedCounts = completedRes?.data?.counts ?? {};

      const baseWaiting = Number(waitingCounts?.waiting ?? 0);
      const baseCompleted = Number(completedCounts?.completed ?? 0);

      const parseCpsList = (res) => {
        const arr = res?.data?.data ?? res?.data ?? [];
        return Array.isArray(arr) ? arr : [];
      };

      // 2) CPS counts
      const countForStatus = async (st) => {
        const res = await cpsAdminSettlementsList({ status: st, limit: 5000 });
        return parseCpsList(res).length;
      };

      const [draftCount, requestedCount, confirmedCount] =
        await Promise.all([
          countForStatus("DRAFT"),
          countForStatus("REQUESTED"),
          countForStatus("CONFIRMED"),
        ]);

      const cpsWaiting = (draftCount ?? 0) + (requestedCount ?? 0);
      const cpsCompleted = confirmedCount ?? 0;

      setCounts({
        waiting: baseWaiting + cpsWaiting,
        completed: baseCompleted + cpsCompleted,
      });
    } catch (e) {
      console.error("loadCountsWithCps error:", e);
      // counts가 null/undefined가 되지 않도록 최소값으로 세팅
      setCounts((prev) => ({
        waiting: Number(prev?.waiting ?? 0),
        completed: Number(prev?.completed ?? 0),
      }));
    }
  };

  const loadCpsItems = async () => {
    const statuses = cpsStatusByTab[currentTab] ?? [];
    if (statuses.length === 0) {
      setCpsItems([]);
      return;
    }
    setCpsLoading(true);
    try {
      const listResults = await Promise.all(
        statuses.map((st) =>
          cpsAdminSettlementsList({
            status: st,
            limit: 500,
          })
        )
      );
      const flat = listResults.flatMap((res) => {
        const arr = res?.data?.data ?? res?.data ?? [];
        return Array.isArray(arr) ? arr : [];
      });
      if (flat.length === 0) {
        setCpsItems([]);
        return;
      }
      const details = await Promise.all(
        flat.map(async (row) => {
          const detailRes = await cpsAdminSettlementDetail(row.cpsSettlementId);
          return detailRes?.data?.data ?? detailRes?.data ?? row;
        })
      );
      setCpsItems(groupCpsByCampaign(details));
    } catch (e) {
      console.error("loadCpsItems error:", e);
      setCpsItems([]);
    } finally {
      setCpsLoading(false);
    }
  };

  const onCpsRequest = async (id) => {
    try {
      await cpsAdminSettlementRequest(id);
      toast({ status: "success", title: "CPS 정산서가 발송되었습니다." });
      await loadCpsItems();
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error?.message ||
        e?.message;
      toast({ status: "error", title: "발송 실패", description: msg });
    }
  };

  const onCpsPayCommissions = async (id) => {
    try {
      setCpsPayBusyId(id);
      await cpsAdminSettlementPayCommissions(id);
      toast({ status: "success", title: "커미션 지급이 완료되었습니다." });
      await loadCpsItems();
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error?.message ||
        e?.message;
      toast({ status: "error", title: "커미션 지급 실패", description: msg });
    } finally {
      setCpsPayBusyId(null);
    }
  };

  // 탭 변경 시 초기화 + 첫 페이지 로드
  useEffect(() => {
    setItems([]);
    setKeyword("");

    loadItems();
    loadCpsItems();
    void loadCountsWithCps();
    // eslint-disable-next-line
  }, [currentTab]);

  // 검색 필터링
  // 🔍 creators/캠페인명 기반 검색 필터링
  const filtered = items
    .filter((item) => String(item?.campaign?.billingModel ?? "").toUpperCase() !== "CPS")
    .map((item) => {
      const creators = item.creators?.filter((c) => {
        const username = c.creator?.username?.toLowerCase() ?? "";
        const title = item.campaign?.title?.toLowerCase() ?? "";
        return (
          username.includes(keyword.toLowerCase()) ||
          title.includes(keyword.toLowerCase())
        );
      });

      if (!creators || creators.length === 0) return null;
      return { ...item, creators };
    })
    .filter(Boolean);

  const filteredCps = cpsItems.filter((item) => {
    const q = keyword.toLowerCase();
    if (!q) return true;
    const campaignTitle = String(item.campaignTitle ?? "").toLowerCase();
    if (campaignTitle.includes(q)) return true;
    return item.creators?.some((c) =>
      String(c.creatorDisplayName ?? "").toLowerCase().includes(q)
    );
  });

  const campaignProps = {
    list: filtered,
    cpsList: filteredCps,
    loading: loading || cpsLoading,
    onCpsRequest,
    onCpsPayCommissions,
    cpsPayBusyId,
  };

  const tabs = [
    { label: `정산 대기 (${counts?.waiting ?? 0})`, path: "wait" },
    { label: `정산 완료 (${counts?.completed ?? 0})`, path: "complete" },
  ];

  const title = ["정산 대기 목록", "정산 완료 목록"];
  const desc = [
    "광고가 종료되어 정산서 발행 대기 목록입니다. 광고주에게 정산서를 발행하고 확인을 받으면 정산이 완료됩니다.",
    "정산이 완료된 캠페인 목록입니다. 크리에이터 및 광고주 정산 내역을 확인할 수 있습니다.",
  ];
  const activeIndex = tabs.findIndex((t) => pathname.includes(t.path));
  const onTabChange = (index) => {
    navigate(`/settlement/${tabs[index].path}`);
  };

  return (
    <Flex flexDir="column" h="100%" overflow="hidden">
      <Flex justify="space-between" alignItems="center" mb="24px">
        <CampaignTabs
          tabLabels={tabs.map((t) => t.label)}
          onChange={onTabChange}
          activeIndex={activeIndex}
        />
        <ReusableSearchBar
          placeholder="정산 검색"
          width="229px"
          onSearch={(v) => setKeyword(v)}
        />
      </Flex>
      <Box mb="16px">
        <Text fontSize="xl" fontWeight="bold" mb="2">
          {title[activeIndex]}
        </Text>
        <Text color="gray.600" mb="6">
          {desc[activeIndex]}
        </Text>
      </Box>
      <Box flex="1" overflowY="auto">
        <Routes>
          <Route path="wait/*" element={<WaitTab {...campaignProps} />} />
          <Route
            path="complete/*"
            element={<CompleteTab {...campaignProps} />}
          />
          <Route path="*" element={<Navigate to="wait" replace />} />
        </Routes>
      </Box>
    </Flex>
  );
}
